import { IncomingMessage } from 'http';
import memoize from 'lodash/memoize';
import { asDynamic } from '../../util/Dynamic';
import { env } from '../../util/env';
import { useServices } from '../Services';
import { Flags } from '../flags';
import { TracingHeaders } from '../tracingHeaders';
import { getGlobalLogger } from './getGlobalLogger.server-only';

export class LoggerService {
  constructor(
    private req: IncomingMessage | undefined,
    private flags: Flags = {},
    private tracingHeaders: TracingHeaders = {},
  ) {}

  private getWinstonLogger = memoize(() => {
    const { req, flags } = this;
    const rootLogger = getGlobalLogger?.();
    if (!rootLogger || !req) return undefined;
    const { url } = req;

    /** When we care about the logs being pretty, we don't need a lot of machine-readable data */
    if (env.PRETTY_PRINT_LOGS) return rootLogger.child({ url });

    return rootLogger.child({
      url,
      flags,
      /** These properties are spelled to match nginx, allowing cross-service log reports  */
      // eslint-disable-next-line camelcase
      client_ip: req.headers['x-real-ip'] || 'unknown',
      'User-Agent': req.headers['user-agent'],
      ...this.tracingHeaders,
    });
  });

  public info = (message: string, payload?: unknown) =>
    this.getWinstonLogger()?.info(message, payload);
  public warn = (message: string, payload?: unknown) =>
    this.getWinstonLogger()?.warn(message, payload);
  public error = (message: string, payload?: unknown) =>
    this.getWinstonLogger()?.error(message, payload);

  public profile = async <T>(
    message: string,
    callback: (addLogContext: (key: string, value: string) => void) => T,
  ) => {
    const profiler = this.getWinstonLogger()?.startTimer();
    const context: Record<string, string> = {};

    const addLogContext = (key: string, value: string) => {
      context[key] = value;
    };

    try {
      return await Promise.resolve(callback(addLogContext));
    } catch (error: unknown) {
      this.error(`Error while ${message}: ${asDynamic(error)?.message ?? String(error)}`, {
        error,
      });

      throw error;
    } finally {
      profiler?.done({ message, context });
    }
  };
}

export const useLogger = () => useServices().logger;
